import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import Icon from './Icon';
import BrandIcon from './BrandIcon';
import Avatar from './Avatar';
import AgencySelector from './AgencySelector';
import OrgBrandModal from './OrgBrandModal';
import VelvetLogo from '../icons/VL_WordmarkOnly_Light.svg';
import VelvetEmblem from '../icons/VL_EmblemOnly_Light.svg';
import { getUserRole, ROLE_PERMISSIONS } from '../utils/rolePermissions';
import { useOrgBrand } from '../context/OrgBrandContext';
import { LIVEKIT_ENABLED } from '../config/features';
import axios from 'axios';
import '../styles/LeftNav.css';

const LeftNav = ({ activeSection, onSectionChange, isCollapsed, onToggleCollapse, isMobileNavOpen = false, user, onLogout, onAgencyChange, onOpenWelcome, onOpenOnboarding, isProxyMode = false }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [openAccordions, setOpenAccordions] = useState({});
  const [hasCalendarAccess, setHasCalendarAccess] = useState(true);
  const [showOrgBrandModal, setShowOrgBrandModal] = useState(false);
  const [collapsedFlyout, setCollapsedFlyout] = useState(null); // Track which flyout menu is open
  const [flyoutFlipped, setFlyoutFlipped] = useState(false);
  const dropdownRef = useRef(null);
  const flyoutRef = useRef(null);
  const navRef = useRef(null); // Reference to this specific LeftNav instance
  const navScrollRef = useRef(null);
  const savedScrollData = useRef(null); // { scrollTop, itemOffsetTop }
  const { selectedBrand, selectedOrganization, setOrganization, setBrand } = useOrgBrand();

  const userRole = getUserRole(user);
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = user.is_admin || isSuperAdmin;
  const isOperator = user.is_operator || isAdmin;
  const isClient = userRole === 'client';

  // Get brand color for vertical bar
  const brandBarColor = selectedBrand?.menu_color || 'var(--gray-500)';

  // Collapse the org and brand rows into one when they represent the same name
  const orgAndBrandNamesMatch = Boolean(
    selectedOrganization?.organization_name &&
    (selectedBrand?.brand_name || selectedBrand?.name) &&
    selectedOrganization.organization_name.trim().toLowerCase() ===
      (selectedBrand.brand_name || selectedBrand.name).trim().toLowerCase()
  );

  // Check if user has calendar access on mount
  useEffect(() => {
    const checkCalendarAccess = async () => {
      try {
        const now = new Date();
        const params = new URLSearchParams({
          timeMin: now.toISOString(),
          timeMax: new Date(now.getTime() + 60000).toISOString(), // Just 1 minute ahead
          maxResults: 1
        });

        const response = await fetch(`/api/calendar/events?${params}`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });

        const data = await response.json();

        if (data.no_calendar_access) {
          setHasCalendarAccess(false);
        }
      } catch (error) {
        // If there's an error checking, assume they have access and let the calendar page handle it
        console.log('Could not check calendar access:', error);
      }
    };

    checkCalendarAccess();
  }, []);

  // Helper function to check if user can see a menu item based on roles
  // If no roles specified, item is visible to everyone
  // roles can be: 'super_admin', 'admin', 'operator', 'client'
  const canUserSee = (allowedRoles) => {
    if (!allowedRoles || allowedRoles.length === 0) return true;
    return allowedRoles.includes(userRole);
  };

  // Check if user can see Velvet Meetings (available to super admins)
  const canSeeLiveKitMeetings = () => {
    return LIVEKIT_ENABLED && isSuperAdmin;
  };

  // Filter sub-items based on role visibility
  const filterSubItems = (subItems) => {
    if (!subItems) return subItems;
    return subItems.filter(item => {
      return canUserSee(item.roles);
    });
  };

  // Determine calendar provider label based on user's auth provider
  const calendarProvider = user?.auth_provider === 'Microsoft' ? 'Microsoft' : 'Google';
  const calendarLabel = `${calendarProvider} Calendar View`;

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'home',
      enabled: true,
      type: 'single'
    },
    {
      id: 'todo',
      label: 'To Do',
      icon: 'check',
      enabled: true,
      type: 'single'
    },
    {
      id: 'calendar',
      label: 'Meetings',
      icon: 'calendar',
      enabled: true,
      type: 'single'
    },
    ...(canSeeLiveKitMeetings() ? [{
      id: 'livekit-meetings',
      label: 'Velvet Meetings',
      icon: 'velvet',
      enabled: true,
      type: 'single'
    }] : []),
    {
      id: 'admin-organizations-brands',
      label: 'Organization',
      icon: 'building',
      enabled: true,
      type: 'single',
      roles: ['super_admin', 'admin']
    },
    {
      id: 'sources-menu',
      label: 'Sources',
      icon: 'sources',
      enabled: true,
      type: 'accordion',
      roles: ['super_admin', 'admin', 'operator'],
      subItems: [
        { id: 'sources', label: 'Manage Sources', enabled: true, roles: ['super_admin', 'admin', 'operator'] },
        { id: 'recordings', label: 'Squadcast', enabled: true, roles: ['super_admin', 'admin', 'operator'] },
        ...(LIVEKIT_ENABLED ? [{ id: 'velvet-recordings', label: 'Velvet Recordings', enabled: true, roles: ['super_admin', 'admin', 'operator'] }] : [])
      ]
    },
    {
      id: 'ideas',
      label: 'Ideas',
      icon: 'lightbulb',
      enabled: true,
      type: 'accordion',
      subItems: [
        { id: 'create-ideas', label: 'Create Ideas', enabled: true, roles: ['super_admin', 'admin', 'operator'] },
        { id: 'operator-review-ideas', label: 'Review Ideas', enabled: true, roles: ['super_admin', 'admin', 'operator'] },
        { id: 'client-review-ideas', label: 'Review Ideas', enabled: true, roles: ['client'] }
      ]
    },
    {
      id: 'content',
      label: 'Content',
      icon: 'content',
      enabled: true,
      type: 'accordion',
      subItems: [
        { id: 'create', label: 'Create Content', enabled: true, roles: ['super_admin', 'admin', 'operator'] },
        { id: 'review-content', label: 'Review Content', enabled: true, roles: ['super_admin', 'admin', 'operator', 'client'] }
      ]
    },
    {
      id: 'schedule-posts',
      label: 'Schedule',
      icon: 'calendar-check-o',
      enabled: true,
      type: 'single',
      roles: ['super_admin', 'admin', 'operator']
    },
    {
      id: 'brand-hub',
      label: 'Brand Hub',
      icon: 'vkontakte-rect',
      enabled: true,
      type: 'accordion',
      roles: ['super_admin', 'admin', 'operator', 'client'],
      subItems: [
        { id: 'assets', label: 'Identity', enabled: true, roles: ['super_admin', 'admin', 'operator'] },
        { id: 'brand-builder', label: 'Brand Voice Designer', enabled: true, roles: ['super_admin', 'client'] }
      ]
    },
    {
      id: 'gemini',
      label: 'Velvet AI',
      icon: 'ai',
      enabled: true,
      type: 'single',
      roles: ['super_admin']
    },
    {
      id: 'agency-hub',
      label: 'Agency Hub',
      icon: 'server',
      enabled: true,
      type: 'single',
      roles: ['super_admin', 'admin']
    }
  ].filter(item => canUserSee(item.roles)).map(item => ({
    ...item,
    subItems: filterSubItems(item.subItems)
  }));

  // Add Admin menu for regular admins only (not super admins)
  if (isAdmin && !isSuperAdmin) {
    menuItems.push({
      id: 'admin',
      label: 'Admin',
      icon: 'admin',
      enabled: true,
      type: 'accordion',
      subItems: [
        { id: 'admin-user-management', label: 'User Management', enabled: true },
        { id: 'proxy-user', label: 'Proxy User', enabled: true },
        { id: 'activity-log', label: 'Activity Logs', enabled: true }
      ]
    });
  }

  // Add Super Admin menu with all admin + super admin features combined
  if (isSuperAdmin) {
    menuItems.push({
      id: 'super-admin',
      label: 'Super Admin',
      icon: 'superadmin',
      enabled: true,
      type: 'accordion',
      subItems: [
        { id: 'admin-user-management', label: 'User Management', enabled: true },
        { id: 'prompt-hub', label: 'Prompt Hub', enabled: true },
        { id: 'proxy-user', label: 'Proxy User', enabled: true },
        { id: 'create-agency', label: 'Create Agency', enabled: true },
        { id: 'casper', label: 'Casper', enabled: true },
        { id: 'guest-completions', label: 'Guest Completions', enabled: true },
        { id: 'activity-log', label: 'Activity Logs', enabled: true },
        { id: 'logs', label: 'System Logs', enabled: true }
      ]
    });
  }

  // Auto-open accordion when activeSection matches a sub-item
  useEffect(() => {
    // For admin's LeftNav: if viewing proxy section, don't respond to activeSection changes
    // This prevents admin's accordion from opening when clicking proxied user's menu
    if (!isProxyMode && activeSection === 'proxy-user') {
      return;
    }

    // Find which accordion contains the active section
    const accordionToOpen = menuItems.find(item =>
      item.type === 'accordion' &&
      item.subItems?.some(subItem => subItem.id === activeSection)
    );

    setOpenAccordions(prev => {
      if (accordionToOpen) {
        // Only update if this accordion isn't already the only one open
        if (Object.keys(prev).length === 1 && prev[accordionToOpen.id]) {
          return prev; // Already correctly set, no change needed
        }
        return { [accordionToOpen.id]: true };
      } else {
        // If activeSection is not in any accordion (e.g., Home), close all accordions
        if (Object.keys(prev).length > 0) {
          return {};
        }
        return prev; // Already empty, no change needed
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, isProxyMode]); // Only re-run when activeSection changes

  const toggleAccordion = (itemId) => {
    setOpenAccordions(prev => {
      if (prev[itemId]) return {};
      return { [itemId]: true };
    });
  };

  const handleItemClick = (item, event) => {
    if (!item.enabled) return;
    saveScrollPosition(event, item.id);

    if (item.type === 'accordion') {
      // If collapsed, show flyout menu instead of expanding inline
      if (isCollapsed) {
        const newFlyoutState = collapsedFlyout === item.id ? null : item.id;
        setCollapsedFlyout(newFlyoutState);

        if (newFlyoutState && event?.currentTarget) {
          const rect = event.currentTarget.getBoundingClientRect();
          const flyoutTop = rect.top;

          // Estimate if flyout will overflow viewport bottom
          const headerHeight = 43;
          const itemHeight = 43;
          const itemsPadding = 16;
          const numItems = item.subItems?.length || 0;
          const estimatedHeight = headerHeight + numItems * itemHeight + itemsPadding;
          setFlyoutFlipped(flyoutTop + estimatedHeight > window.innerHeight);

          // Always anchor to rect.top; CSS transform handles the flip offset
          document.documentElement.style.setProperty('--flyout-menu-top', `${flyoutTop}px`);
        } else {
          setFlyoutFlipped(false);
        }
        return;
      }

      // Check if the active section is within this accordion
      const isActiveSectionInAccordion = item.subItems?.some(
        subItem => subItem.id === activeSection
      );

      // Don't allow closing the accordion if user is on a panel within it
      // This prevents jitter/animation when trying to close
      if (isActiveSectionInAccordion && openAccordions[item.id]) {
        return; // Do nothing - prevent toggle
      }

      toggleAccordion(item.id);
    } else {
      onSectionChange(item.id);
    }
  };

  const handleSubItemClick = (subItem, event) => {
    if (subItem.enabled) {
      saveScrollPosition(event, subItem.id);
      onSectionChange(subItem.id);
      setCollapsedFlyout(null);
      setFlyoutFlipped(false);
    }
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleLogout = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowDropdown(false);
    if (onLogout) {
      onLogout();
    }
  };

  const handleProfileClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowDropdown(false);
    onSectionChange('profile');
  };

  const handleWelcomeClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowDropdown(false);
    if (onOpenWelcome) {
      onOpenWelcome();
    }
  };

  const handleOnboardingClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowDropdown(false);
    if (onOpenOnboarding) {
      onOpenOnboarding();
    }
  };

  const handleOrgBrandConfirm = async (org, brand) => {
    try {
      // Set organization context on backend
      await axios.post('/api/user/set-organization-context', {
        organization_id: org._id
      }, {
        withCredentials: true
      });

      // Update context
      setOrganization(org, isProxyMode);
      setBrand(brand, isProxyMode);
    } catch (error) {
      console.error('Error setting org/brand:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Close flyout menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (flyoutRef.current && !flyoutRef.current.contains(event.target)) {
        setCollapsedFlyout(null);
        setFlyoutFlipped(false);
      }
    };

    if (collapsedFlyout) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [collapsedFlyout]);

  // Close flyout when nav expands
  useEffect(() => {
    if (!isCollapsed) {
      setCollapsedFlyout(null);
    }
  }, [isCollapsed]);

  // Restore scroll so the clicked item stays at the same visual offset after re-render
  useLayoutEffect(() => {
    if (savedScrollData.current && navScrollRef.current) {
      const { visualOffset, sectionId } = savedScrollData.current;
      const el = navScrollRef.current.querySelector(`[data-section="${sectionId}"]`);
      if (el) {
        navScrollRef.current.scrollTop = el.offsetTop - visualOffset;
      }
      savedScrollData.current = null;
    }
  });

  const saveScrollPosition = (event, sectionId) => {
    if (!navScrollRef.current || !event?.currentTarget) return;
    const el = event.currentTarget;
    // Only track elements that live inside the scroll container
    if (!navScrollRef.current.contains(el)) return;
    savedScrollData.current = {
      sectionId,
      visualOffset: el.offsetTop - navScrollRef.current.scrollTop,
    };
  };

  return (
    <aside ref={navRef} className={`left-nav ${isCollapsed ? 'collapsed' : ''} ${isProxyMode ? 'proxy-mode' : ''} ${isMobileNavOpen ? 'mobile-open' : ''}`}>
      <div className="left-nav-header">
        <img src={isCollapsed ? VelvetEmblem : VelvetLogo} alt="Velvet" className="app-logo" />
      </div>

      <div className="nav-sections-wrapper" ref={navScrollRef}>
      {/* First section: Dashboard, To Do, Meetings, Velvet Meetings, Organization, Admin/Super Admin, Velvet AI */}
      <nav className="left-nav-menu left-nav-section-primary">
        {menuItems.filter(item =>
          item.id === 'dashboard' ||
          item.id === 'todo' ||
          item.id === 'calendar' ||
          item.id === 'livekit-meetings' ||
          item.id === 'admin-organizations-brands' ||
          item.id === 'admin' ||
          item.id === 'super-admin' ||
          item.id === 'gemini' ||
          item.id === 'agency-hub'
        ).map((item) => {
          return (
            <React.Fragment key={item.id}>
              <div className="nav-menu-item-wrapper">
                {item.type === 'accordion' && openAccordions[item.id] && !isCollapsed ? (
                  <div className="nav-accordion-container">
                    <div
                      className={`nav-menu-item has-submenu accordion-open ${!item.enabled ? 'disabled' : ''}`}
                      onClick={(e) => handleItemClick(item, e)}
                      data-section={item.id}
                    >
                      <Icon name={item.icon} size="1.25rem" />
                      <span className="nav-label">{item.label}</span>
                    </div>
                    <div className="nav-submenu">
                      <div className="nav-submenu-wrapper">
                        {item.subItems.map((subItem) => (
                          <div
                            key={subItem.id}
                            className={`nav-submenu-item ${
                              activeSection === subItem.id ? 'active' : ''
                            } ${!subItem.enabled ? 'disabled' : ''}`}
                            onClick={(e) => handleSubItemClick(subItem, e)}
                            data-section={subItem.id}
                          >
                            <span className="nav-sublabel">{subItem.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`nav-menu-item ${
                      (item.type === 'single' && activeSection === item.id) ||
                      (item.type === 'accordion' && isCollapsed && item.subItems?.some(sub => sub.id === activeSection)) ||
                      (item.type === 'accordion' && isCollapsed && collapsedFlyout === item.id)
                        ? 'active'
                        : ''
                    } ${!item.enabled ? 'disabled' : ''} ${
                      item.type === 'accordion' ? 'has-submenu' : ''
                    } ${openAccordions[item.id] ? 'accordion-open' : ''}`}
                    onClick={(e) => handleItemClick(item, e)}
                    title={isCollapsed ? item.label : ''}
                    data-section={item.id}
                  >
                    <Icon name={item.icon} size="1.25rem" />
                    <span className="nav-label">{item.label}</span>
                  </div>
                )}

                {/* Flyout menu for collapsed state */}
                {item.type === 'accordion' && collapsedFlyout === item.id && isCollapsed && (
                  <div className={`nav-flyout-menu${flyoutFlipped ? ' flipped' : ''}`} ref={flyoutRef}>
                    <div className="nav-flyout-header">
                      <span className="nav-flyout-title">{item.label}</span>
                    </div>
                    <div className="nav-flyout-items">
                      <div className="nav-flyout-items-wrapper">
                        {item.subItems.map((subItem) => (
                          <div
                            key={subItem.id}
                            className={`nav-flyout-item ${
                              activeSection === subItem.id ? 'active' : ''
                            } ${!subItem.enabled ? 'disabled' : ''}`}
                            onClick={(e) => handleSubItemClick(subItem, e)}
                            data-section={subItem.id}
                          >
                            <span className="nav-flyout-label">{subItem.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </nav>

      <div className="nav-sections-spacer" />
      {/* Second section: Org Selector, Brand Selector, and other menus */}
      <div className="left-nav-section-secondary">
        <div className="brand-color-bar" style={{ backgroundColor: brandBarColor }}></div>

        <nav className="left-nav-menu">
          {/* Org/Brand Selectors */}
          <div className="org-brand-selectors" data-onboarding="org-brand-selector">
            {orgAndBrandNamesMatch ? (
              <div className="org-selector" data-onboarding="org-selector" onClick={() => setShowOrgBrandModal(true)} title={isCollapsed ? selectedOrganization.organization_name : ''}>
                <div className="org-selector-icon">
                  {selectedBrand?.icon_url || selectedOrganization?.icon_url ? (
                    <BrandIcon src={selectedBrand?.icon_url || selectedOrganization?.icon_url} alt="" className="org-icon-img" />
                  ) : (
                    <Icon name="building" size="1rem" />
                  )}
                </div>
                {!isCollapsed && (
                  <span className="org-selector-label">
                    {selectedOrganization.organization_name}
                  </span>
                )}
              </div>
            ) : (
              <>
                <div className="org-selector" data-onboarding="org-selector" onClick={() => setShowOrgBrandModal(true)} title={isCollapsed ? (selectedOrganization?.organization_name || 'Select Organization') : ''}>
                  <div className="org-selector-icon">
                    {selectedOrganization?.icon_url ? (
                      <BrandIcon src={selectedOrganization.icon_url} alt="" className="org-icon-img" />
                    ) : (
                      <Icon name="building" size="1rem" />
                    )}
                  </div>
                  {!isCollapsed && (
                    <>
                      <span className="org-selector-label">
                        {selectedOrganization?.organization_name || 'Select Organization'}
                      </span>
                    </>
                  )}
                </div>

                <div className="brand-selector" data-onboarding="brand-selector" onClick={() => setShowOrgBrandModal(true)} title={isCollapsed ? (selectedBrand?.brand_name || selectedBrand?.name || 'Select Brand') : ''}>
                  <div className="brand-selector-icon">
                    {selectedBrand?.icon_url ? (
                      <BrandIcon src={selectedBrand.icon_url} alt="" className="brand-icon-img" />
                    ) : (
                      <Icon name="tag" size="1rem" />
                    )}
                  </div>
                  {!isCollapsed && (
                    <>
                      <span className="brand-selector-label">
                        {selectedBrand?.brand_name || selectedBrand?.name || 'Select Brand'}
                      </span>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Other menu items: Sources, Ideas, Content, Brand Hub */}
          {menuItems.filter(item =>
            item.id !== 'dashboard' &&
            item.id !== 'todo' &&
            item.id !== 'calendar' &&
            item.id !== 'livekit-meetings' &&
            item.id !== 'admin-organizations-brands' &&
            item.id !== 'admin' &&
            item.id !== 'super-admin' &&
            item.id !== 'gemini' &&
            item.id !== 'agency-hub'
          ).map((item) => (
            <React.Fragment key={item.id}>
              <div className="nav-menu-item-wrapper">
                {item.type === 'accordion' && openAccordions[item.id] && !isCollapsed ? (
                  <div className="nav-accordion-container">
                    <div
                      className={`nav-menu-item has-submenu accordion-open ${!item.enabled ? 'disabled' : ''}`}
                      onClick={(e) => handleItemClick(item, e)}
                      data-section={item.id}
                    >
                      <Icon name={item.icon} size="1.25rem" />
                      <span className="nav-label">{item.label}</span>
                    </div>
                    <div className="nav-submenu">
                      <div className="nav-submenu-wrapper">
                        {item.subItems.map((subItem) => (
                          <div
                            key={subItem.id}
                            className={`nav-submenu-item ${
                              activeSection === subItem.id ? 'active' : ''
                            } ${!subItem.enabled ? 'disabled' : ''}`}
                            onClick={(e) => handleSubItemClick(subItem, e)}
                            data-section={subItem.id}
                          >
                            <span className="nav-sublabel">{subItem.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`nav-menu-item ${
                      (item.type === 'single' && activeSection === item.id) ||
                      (item.type === 'accordion' && isCollapsed && item.subItems?.some(sub => sub.id === activeSection)) ||
                      (item.type === 'accordion' && isCollapsed && collapsedFlyout === item.id)
                        ? 'active'
                        : ''
                    } ${!item.enabled ? 'disabled' : ''} ${
                      item.type === 'accordion' ? 'has-submenu' : ''
                    } ${openAccordions[item.id] ? 'accordion-open' : ''}`}
                    onClick={(e) => handleItemClick(item, e)}
                    title={isCollapsed ? item.label : ''}
                    data-section={item.id}
                  >
                    <Icon name={item.icon} size="1.25rem" />
                    <span className="nav-label">{item.label}</span>
                  </div>
                )}

                {/* Flyout menu for collapsed state */}
                {item.type === 'accordion' && collapsedFlyout === item.id && isCollapsed && (
                  <div className={`nav-flyout-menu${flyoutFlipped ? ' flipped' : ''}`} ref={flyoutRef}>
                    <div className="nav-flyout-header">
                      <span className="nav-flyout-title">{item.label}</span>
                    </div>
                    <div className="nav-flyout-items">
                      <div className="nav-flyout-items-wrapper">
                        {item.subItems.map((subItem) => (
                          <div
                            key={subItem.id}
                            className={`nav-flyout-item ${
                              activeSection === subItem.id ? 'active' : ''
                            } ${!subItem.enabled ? 'disabled' : ''}`}
                            onClick={(e) => handleSubItemClick(subItem, e)}
                            data-section={subItem.id}
                          >
                            <span className="nav-flyout-label">{subItem.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </React.Fragment>
          ))}
        </nav>
      </div>
      </div>

      <OrgBrandModal
        isOpen={showOrgBrandModal}
        onClose={() => setShowOrgBrandModal(false)}
        onConfirm={handleOrgBrandConfirm}
        onManageOrganizations={isAdmin ? (org, brand) => {
          setShowOrgBrandModal(false);
          const deepLinkParams = {};
          if (org) deepLinkParams.orgId = String(org._id || org.id);
          if (brand) {
            deepLinkParams.brandId = String(brand._id || brand.id);
            deepLinkParams.editBrand = '1';
          }
          onSectionChange('admin-organizations-brands', deepLinkParams);
        } : null}
        user={user}
        isProxyMode={isProxyMode}
        currentOrganization={selectedOrganization}
        currentBrand={selectedBrand}
      />

      <div className="left-nav-bottom" ref={dropdownRef}>
        <button className="collapse-toggle" onClick={onToggleCollapse} title={isCollapsed ? 'Expand' : 'Collapse'}>
          <Icon name={isCollapsed ? 'right-open' : 'left-open'} size="1.25rem" />
        </button>
        {isSuperAdmin && (
          <div className={`agency-selector-wrapper ${isCollapsed ? 'collapsed' : ''}`}>
            <AgencySelector user={user} onAgencyChange={onAgencyChange} />
          </div>
        )}
        {showDropdown && (
          <div className="profile-dropdown">
            <button className="dropdown-item profile-btn" onClick={handleProfileClick}>
              <Icon name="user" size="1.25rem" />
              <span className="profile-label">Profile</span>
            </button>
            <button className="dropdown-item notifications-btn" onClick={() => { setShowDropdown(false); onSectionChange('notifications'); }}>
              <Icon name="bell" size="1.25rem" />
              <span className="notifications-label">Notifications</span>
            </button>
            <button className="dropdown-item welcome-btn" onClick={handleWelcomeClick}>
              <Icon name="velvet" size="1.25rem" />
              <span className="welcome-label">Welcome</span>
            </button>
            <button className="dropdown-item onboarding-btn" onClick={handleOnboardingClick}>
              <Icon name="info-circled" size="1.25rem" />
              <span className="onboarding-label">Onboarding</span>
            </button>
            <button className="dropdown-item logout-btn" onClick={handleLogout}>
              <Icon name="logout" size="1.25rem" />
              <span className="logout-label">Logout</span>
            </button>
          </div>
        )}
        <div className="nav-user-profile" onClick={toggleDropdown}>
          <Avatar user={user} size="md" fallbackType="icon" className="nav-user-avatar" />
          <div className="nav-user-info">
            <span className="nav-user-name">{user?.name || 'User'}</span>
            <span className="nav-user-role">
              {user ? ROLE_PERMISSIONS[getUserRole(user)]?.name || 'User' : 'User'}
            </span>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default LeftNav;
